import { useState } from "react";
import { formatDate } from "../../lib/format";
import { Trans, useTranslation } from "react-i18next";
import type { Invitation } from "../../api/admin";
import { useAuth } from "../../auth/AuthContext";
import { InviteUserForm } from "../../components/InviteUserForm";

/**
 * Standalone /admin/invitations page. Most flows go through the "+ Invite user"
 * button on the Users page now; this URL is kept as a deep-link fallback.
 */
export default function AdminInvitationsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [recent, setRecent] = useState<Invitation | null>(null);

  // The route is reachable on section:invitations:read, while sending an invite
  // needs section:invitations:write plus users:invite_any. Without those the page
  // keeps its heading but promises nothing: no card, and a line saying why.
  const canSend = hasPermission("section:invitations:write") && hasPermission("users:invite_any");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 640 }}>
      <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.invitations.title", "Invite a user")}</h1>
      <p style={{ color: "var(--ink-3)", fontSize: 14, margin: 0 }}>
        {canSend
          ? t("admin.invitations.subtitle", "The recipient gets an email with a 7-day link to set up their account.")
          : t(
              "admin.invitations.readOnlyHint",
              "Vaš nalog pozivnice samo čita. Poziv šalje onaj ko ima pravo upisa u pozivnice.",
            )}
      </p>

      {canSend && (
        <div style={card}>
          <InviteUserForm onCreated={(inv) => setRecent(inv)} />
        </div>
      )}

      {recent && (
        <div style={banner}>
          <Trans
            i18nKey="admin.invitations.sentBanner"
            defaults="Invitation sent to <1>{{email}}</1>. Expires {{expiresAt}}."
            values={{ email: recent.email, expiresAt: formatDate(recent.expiresAt) }}
            components={{ 1: <strong /> }}
          />
        </div>
      )}
    </div>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  padding: 20,
  borderRadius: 10,
  boxShadow: "var(--shadow-1)",
};
const banner: React.CSSProperties = {
  padding: 12,
  background: "var(--ok-bg)",
  color: "var(--ok)",
  borderRadius: 8,
  fontSize: 14,
};
